import {
  type AssessmentDefinition,
  type AssessmentRecord,
  type AssessmentType,
  type DomainScoreConfig,
  type AssessmentResultSection,
  type AssessmentData,
} from "./types";
import { getVinelandAdaptiveLevel, getVinelandQualitativeDescriptor } from "./utils";
import { AFLS_STEP_CONFIGS } from "./dataEntryConfig";

// ─── AFLS Result Section Builder ─────────────────────────────────────────────

function aflsCamelToSnake(str: string): string {
  return str.replace(/([A-Z])/g, "_$1").toLowerCase();
}

/**
 * Dynamically builds AFLS result sections from the step config so that
 * adding or renaming skills only requires updating `dataEntryConfig.ts`.
 * Each section corresponds to one domain (step), with:
 *  - First field: domain total formatted as `score/maxScore`
 *  - Remaining fields: individual sub-skills formatted as `score / maxScore`
 */
function buildAflsResultSections(): AssessmentResultSection[] {
  return AFLS_STEP_CONFIGS.map((stepConfig) => {
    const domainKey = aflsCamelToSnake(stepConfig.domainTotalField);
    const domainMaxScore = stepConfig.skills.find((s) => s.isTotal)?.maxScore ?? 0;

    return {
      title: stepConfig.title,
      showSectionHero: true,
      fields: [
        {
          key: domainKey,
          label: `${stepConfig.title} total`,
          compute: (data: AssessmentData) => {
            const val = data[domainKey];
            return val != null ? `${val}/${domainMaxScore}` : `-/${domainMaxScore}`;
          },
        },
        ...stepConfig.skills
          .filter(
            (skill): skill is typeof skill & { apiField: string } =>
              !skill.isTotal && !!skill.apiField
          )
          .map((skill) => {
            const fieldKey = aflsCamelToSnake(skill.apiField);
            return {
              key: fieldKey,
              label: skill.name,
              compute: (data: AssessmentData) => {
                const val = data[fieldKey];
                return val != null ? `${val} / ${skill.maxScore}` : `- / ${skill.maxScore}`;
              },
            };
          }),
      ],
    };
  });
}

export const DOMAIN_SCORE_CONFIG: DomainScoreConfig[] = [
  {
    key: "adaptive_behavior_composite_standard_score",
    label: "Adaptive behavior composite",
    graphLabel: "Adaptive behavior composite",
    borderColor: "rgb(142, 66, 202)",
    backgroundColor: "rgba(142, 66, 202, 0.5)",
  },
  {
    key: "communication_score",
    label: "Communication score",
    graphLabel: "Communication score",
    borderColor: "rgb(255, 193, 7)",
    backgroundColor: "rgba(255, 193, 7, 0.5)",
  },
  {
    key: "daily_living_score",
    label: "Daily living score",
    graphLabel: "Daily living score",
    borderColor: "rgb(244, 67, 54)",
    backgroundColor: "rgba(244, 67, 54, 0.5)",
  },
  {
    key: "socialization_score",
    label: "Socialization score",
    graphLabel: "Socialization score",
    borderColor: "rgb(76, 175, 80)",
    backgroundColor: "rgba(76, 175, 80, 0.5)",
  },
  {
    key: "motor_score",
    label: "Motor skills",
    graphLabel: "Motor skills",
    borderColor: "rgb(33, 150, 243)",
    backgroundColor: "rgba(33, 150, 243, 0.5)",
  },
];

// ─── Socially Savvy Result Section Builder ───────────────────────────────────

/**
 * Each Socially Savvy section uses showSectionHero so the domain total renders
 * as a prominent header. Individual sub-scores are always shown; null values
 * display as "Not Tested" (handled by readValue in AssessmentResults).
 *
 * Max scores per domain = (number of items) × 3, since each item scores 0–3.
 * JA=27 | SP=72 | SR=51 | SE=18 | SL=72 | CGB=66 | NSL=18
 */
function buildSociallySavvyResultSections(): AssessmentResultSection[] {
  function domainTotal(key: string, max: number): AssessmentResultSection["fields"][0] {
    return {
      key,
      label: key
        .replace(/_total$/, "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase()),
      compute: (data: AssessmentData) => {
        const val = data[key];
        return val != null ? `${val}/${max}` : null;
      },
    };
  }

  return [
    {
      title: "Joint Attention",
      showSectionHero: true,
      fields: [
        domainTotal("joint_attention_total", 27),
        { key: "ja_orients_when_object_presented", label: "Orients when an object is presented" },
        {
          key: "ja_repeats_own_behavior_to_maintain_interaction",
          label: "Repeats own behavior to maintain social interaction",
        },
        {
          key: "ja_repeats_action_with_toy",
          label: "Repeats action with toy to maintain social interaction",
        },
        { key: "ja_uses_eye_gaze", label: "Uses eye gaze to maintain social interaction" },
        { key: "ja_follows_point_or_gesture", label: "Follows point or gesture to objects" },
        { key: "ja_follows_eye_gaze", label: "Follows eye gaze to objects" },
        {
          key: "ja_shows_objects_makes_eye_contact",
          label: "Shows others objects and makes eye contact to share interest",
        },
        {
          key: "ja_points_makes_eye_contact",
          label: "Points to objects and makes eye contact to share interest",
        },
        {
          key: "ja_comments_on_self_or_others",
          label: "Comments on what self or others are doing",
        },
      ],
    },
    {
      title: "Social Play",
      showSectionHero: true,
      fields: [
        domainTotal("social_play_total", 72),
        {
          key: "sp_engages_in_social_interactive_games",
          label: "Engages in social interactive games",
        },
        {
          key: "sp_plays_parallel_closed_ended",
          label: "Plays parallel for five to ten minutes, close to peers with close-ended toys",
        },
        {
          key: "sp_plays_parallel_open_ended",
          label: "Plays parallel for five to ten minutes, close to peers with open-ended toys",
        },
        { key: "sp_shares_toys", label: "Shares toys/materials" },
        {
          key: "sp_plays_cooperatively_closed_ended",
          label: "Plays cooperatively for five to ten minutes with close-ended toys",
        },
        {
          key: "sp_plays_cooperatively_open_ended",
          label: "Plays cooperatively for five to ten minutes with open-ended toys",
        },
        {
          key: "sp_takes_turns_structured_game",
          label:
            "Takes turns as part of a structured game and sustains attention until completion of the game",
        },
        {
          key: "sp_plays_outdoor_games_with_group",
          label: "Plays outdoor games with a group until the completion of the activity",
        },
        {
          key: "sp_stops_action_when_requested_by_peer",
          label: "Stops action when requested by a peer",
        },
        {
          key: "sp_ends_structured_play_appropriately",
          label: "Ends structured play/game with peer appropriately",
        },
        {
          key: "sp_takes_role_in_imaginative_play",
          label:
            "Takes a role in an imaginative play theme and sustains it, both verbally and nonverbally, for up to three to five actions",
        },
        { key: "sp_trades_toys", label: "Trades toys/materials" },
        { key: "sp_invites_peer_to_play", label: "Invites peer to play in a preferred activity" },
        {
          key: "sp_approaches_peers_joins_ongoing_activity",
          label: "Approaches peers and appropriately joins in the ongoing activity",
        },
        {
          key: "sp_accepts_invitation_to_play_peers_choice",
          label: "Accepts invitation to play in an activity of peer's choice",
        },
        { key: "sp_accepts_losing", label: "Accepts losing games or getting called 'out'" },
        {
          key: "sp_remains_engaged_during_unstructured",
          label: "Remains appropriately engaged during unstructured times",
        },
        {
          key: "sp_follows_changes_in_play_ideas",
          label:
            "Follows changes in play ideas of others and sustains the changes during open-ended play",
        },
        {
          key: "sp_plays_games_involving_person_being_it",
          label: "Appropriately plays games involving a person being 'It'",
        },
        {
          key: "sp_demonstrates_flexibility_in_rules",
          label:
            "Demonstrates flexibility in following changes in the rules of a game or in accepting novel ideas from peers",
        },
        {
          key: "sp_plans_play_scheme_with_peer",
          label: "Plans a play scheme with a peer and follows it through",
        },
        {
          key: "sp_identifies_friends_gives_explanation",
          label: "Identifies children who are their friends and can give a simple explanation why",
        },
        {
          key: "sp_accepts_different_likes_and_interests",
          label:
            "Appropriately accepts that others' likes and interests may be different from their own",
        },
        {
          key: "sp_wins_without_bragging",
          label: "Wins without making bragging comments/gestures",
        },
      ],
    },
    {
      title: "Self Regulation",
      showSectionHero: true,
      fields: [
        domainTotal("self_regulation_total", 51),
        { key: "sr_responds_to_name_consistently", label: "Responds to name consistently" },
        {
          key: "sr_makes_transitions_with_reminders",
          label: "Makes transitions between activities with reminders",
        },
        {
          key: "sr_waits_briefly_with_reminders",
          label: "Waits briefly for turn with reminders and prompts",
        },
        {
          key: "sr_calms_down_with_comfort",
          label: "Calms down with comfort provided by familiar adult",
        },
        {
          key: "sr_resolves_conflict_by_requesting_adult",
          label: "Attempts to resolve conflict by requesting adult assistance",
        },
        {
          key: "sr_follows_classroom_expectations_transitions",
          label: "Follows classroom expectations and demonstrates flexibility during transitions",
        },
        {
          key: "sr_demonstrates_flexibility_different_than_planned",
          label: "Demonstrates flexibility when things are different than planned",
        },
        {
          key: "sr_demonstrates_flexibility_interrupted",
          label: "Demonstrates flexibility when preferred activities are interrupted",
        },
        {
          key: "sr_responds_to_feedback_without_challenging_behavior",
          label: "Responds to feedback/correction without exhibiting challenging behaviors",
        },
        {
          key: "sr_responds_to_mistakes_without_challenging_behavior",
          label:
            "Responds to mistakes made by self or others without exhibiting challenging behaviors",
        },
        {
          key: "sr_demonstrates_awareness_of_space",
          label: "Demonstrates awareness of own and other's space",
        },
        {
          key: "sr_modifies_behavior_in_response_to_feedback",
          label: "Modifies behavior in response to feedback",
        },
        {
          key: "sr_uses_appropriate_words_to_turn_down_requests",
          label: "Uses appropriate words and voice tone to turn down requests from others",
        },
        {
          key: "sr_advocates_for_self_without_challenging_behavior",
          label: "Advocates for oneself without exhibiting challenging behaviors",
        },
        {
          key: "sr_asks_for_help_during_novel_activities",
          label: "Asks for help during novel or challenging activities",
        },
        {
          key: "sr_waits_for_help_without_challenging_behavior",
          label:
            "Waits for help, for a requested item, or when directed to for up to one minute without exhibiting challenging behaviors",
        },
        { key: "sr_avoids_perseveration", label: "Avoids perseveration on a topic or question" },
      ],
    },
    {
      title: "Social / Emotional",
      showSectionHero: true,
      fields: [
        domainTotal("social_emotional_total", 18),
        {
          key: "se_recognizes_emotions",
          label: "Recognizes emotions in others and self (e.g., happy, sad)",
        },
        {
          key: "se_gives_explanation_for_emotional_state",
          label: "Gives a simple explanation for the emotional state of self and others when asked",
        },
        { key: "se_shows_empathy", label: "Shows empathy toward others" },
        {
          key: "se_expresses_negative_emotions_without_challenging",
          label: "Expresses negative emotions without exhibiting challenging behaviors",
        },
        {
          key: "se_expresses_appropriate_enthusiasm",
          label:
            "Expresses appropriate level of enthusiasm about the actions or belongings of others",
        },
        {
          key: "se_anticipates_peer_response",
          label: "Anticipates how a peer might respond to his behavior and responds accordingly",
        },
      ],
    },
    {
      title: "Social Language",
      showSectionHero: true,
      fields: [
        domainTotal("social_language_total", 72),
        { key: "sl_responds_to_greetings", label: "Responds to greetings/partings" },
        {
          key: "sl_follows_directions_involving_named_adults",
          label: "Follows directions involving named adults or peers",
        },
        { key: "sl_initiates_greetings", label: "Initiates greetings/partings" },
        { key: "sl_addresses_peers_by_name", label: "Addresses peers by name" },
        {
          key: "sl_answers_social_questions",
          label: "Answers social questions (e.g., name, age, family names, pet names)",
        },
        {
          key: "sl_asks_social_questions",
          label: "Asks social questions (e.g., name, age, family names, pet names)",
        },
        {
          key: "sl_asks_concrete_questions",
          label: "Asks concrete questions about an item or information shared by others",
        },
        { key: "sl_requests_attention", label: "Requests attention" },
        {
          key: "sl_gains_listener_attention_appropriately",
          label: "Gains listener attention appropriately",
        },
        { key: "sl_responds_to_initiations", label: "Responds to initiations from others" },
        {
          key: "sl_answers_questions_about_ongoing_activities",
          label: "Answers questions about ongoing activities",
        },
        {
          key: "sl_shares_information_about_self",
          label: "Shares information about self, family, and major events",
        },
        {
          key: "sl_answers_more_than_five_questions_on_preferred_topic",
          label: "Answers more than five questions on a preferred topic",
        },
        { key: "sl_makes_reciprocal_comments", label: "Makes reciprocal comments" },
        {
          key: "sl_shares_info_about_past_or_future",
          label: "Shares information about immediate past or future events",
        },
        {
          key: "sl_maintains_conversation_three_to_four_exchanges",
          label:
            "Answers questions, asks questions, or makes comments to maintain conversation for three to four exchanges",
        },
        {
          key: "sl_responds_when_peer_changes_topic",
          label: "Responds appropriately when a peer changes topic",
        },
        {
          key: "sl_directs_body_and_eyes_when_speaking",
          label: "Directs body and eyes toward social partner when speaking",
        },
        {
          key: "sl_directs_body_and_eyes_when_listening",
          label: "Directs body and eyes toward social partner when listening",
        },
        { key: "sl_speaks_using_polite_phrases", label: "Speaks using polite phrases" },
        { key: "sl_accepts_people_who_are_different", label: "Accepts people who are different" },
        {
          key: "sl_seeks_to_repair_social_breakdowns",
          label: "Seeks to repair or clarify breakdowns in social interactions",
        },
        {
          key: "sl_converses_on_age_appropriate_topics",
          label: "Converses on age-appropriate topics",
        },
        {
          key: "sl_uses_contextually_appropriate_language",
          label: "Uses contextually appropriate language/introduces topic",
        },
      ],
    },
    {
      title: "Classroom / Group Behavior",
      showSectionHero: true,
      fields: [
        domainTotal("classroom_group_behavior_total", 66),
        {
          key: "cg_follows_schedule_and_classroom_rules",
          label: "Follows schedule and classroom rules (including playground rules)",
        },
        {
          key: "cg_follows_verbal_directions",
          label: "Follows verbal directions as part of classroom routines or activities",
        },
        {
          key: "cg_recognizes_belongings",
          label: "Recognizes belongings of own, others, and group",
        },
        {
          key: "cg_responds_to_teacher",
          label: "Responds to teacher by looking or coming when directly or indirectly cued",
        },
        {
          key: "cg_imitates_peer_leading_songs",
          label: "Imitates a peer who is leading songs/activities",
        },
        { key: "cg_responds_to_indirect_cueing", label: "Responds to indirect cueing" },
        {
          key: "cg_uses_playground_equipment_appropriately",
          label: "Uses playground equipment appropriately",
        },
        { key: "cg_helps_others", label: "Helps others, both spontaneously and when asked" },
        {
          key: "cg_remains_in_place_in_group",
          label: "Remains in place in a group until called by teacher",
        },
        {
          key: "cg_prepares_for_activity",
          label: "Prepares for activity by locating area/materials",
        },
        {
          key: "cg_follows_directions_during_novel_activities",
          label: "Follows directions during novel activities",
        },
        {
          key: "cg_gives_directions_during_novel_activities",
          label: "Gives directions during novel activities",
        },
        {
          key: "cg_stays_in_place_in_line",
          label: "Stays in place when walking in line and maintains pace with group",
        },
        {
          key: "cg_repeats_words_actions_from_song",
          label: "Repeats words/actions from a song, book, or play activity",
        },
        {
          key: "cg_accepts_different_rules_for_peers",
          label: "Accepts that some peers may follow different rules or schedules",
        },
        {
          key: "cg_asks_permission_to_use_others_possessions",
          label: "Asks permission to use others' possessions",
        },
        {
          key: "cg_attends_to_small_group_hands_on_activity",
          label: "Attends to small-group, teacher-led, hands-on activity for ten minutes",
        },
        { key: "cg_sits_quietly_in_circle", label: "Sits quietly in circle for ten minutes" },
        {
          key: "cg_attends_to_small_group_listening_activity",
          label: "Attends to small-group, teacher-led listening activity for ten minutes",
        },
        {
          key: "cg_responds_together_with_group",
          label: "Responds together with group to teacher or peer leading activity",
        },
        {
          key: "cg_follows_two_to_three_step_verbal_directions",
          label: "Follows basic two- to three-step verbal directions in a group",
        },
        { key: "cg_passes_items_to_peers", label: "Passes items to peers" },
      ],
    },
    {
      title: "Nonverbal Social Language",
      showSectionHero: true,
      fields: [
        domainTotal("nonverbal_social_language_total", 18),
        {
          key: "nvsl_reciprocates_nonverbal_interactions",
          label: "Reciprocates nonverbal interactions",
        },
        { key: "nvsl_initiates_nonverbal_interactions", label: "Initiates nonverbal interactions" },
        {
          key: "nvsl_identifies_basic_actions_without_words",
          label: "Identifies basic actions without words",
        },
        {
          key: "nvsl_demonstrates_appropriate_level_of_affection",
          label:
            "Demonstrates an appropriate level of affection based on history, relationship, and familiarity with the person",
        },
        {
          key: "nvsl_follows_basic_gestures_and_nonverbal_cues",
          label: "Follows basic gestures and nonverbal cues",
        },
        {
          key: "nvsl_modifies_behavior_based_on_body_language",
          label: "Modifies own behavior based on the body language, actions, or eye gaze of others",
        },
      ],
    },
  ];
}

const START_ASSESSMENT_FILL_OUT_BASE = "https://forms.getanswersnow.com/t";

export const ASSESSMENT_DEFINITIONS: AssessmentDefinition[] = [
  {
    type: "vineland",
    title: "Vineland Adaptive Behavior Scales, Third Edition",
    description:
      "Measures adaptive skills like communication, daily living, and socialization to evaluate independence and track meaningful progress.",
    primaryActionLabel: "View scores",
    chart: {
      title: "ABC and domain score profile",
      description: "This graph shows the ABC and domain scores over time.",
      variant: "line",
      yAxisLabels: ["Standard score"],
      datasets: DOMAIN_SCORE_CONFIG.map((score) => ({
        label: score.graphLabel,
        data: [],
        borderColor: score.borderColor,
        backgroundColor: score.backgroundColor,
      })),
    },
    resultSections: [
      {
        title: "Scores",
        fields: [
          {
            key: "adaptive_behavior_composite_standard_score",
            label: "Adaptive behavior composite standard score",
          },
          {
            key: "abc_adaptive_level",
            label: "Adaptive behavior composite adaptive level",
            compute: (data) =>
              getVinelandAdaptiveLevel(data.adaptive_behavior_composite_standard_score),
          },
          { key: "communication_score", label: "Communication score" },
          {
            key: "communication_adaptive_level",
            label: "Communication adaptive level",
            compute: (data) => getVinelandAdaptiveLevel(data.communication_score),
          },
          { key: "daily_living_score", label: "Daily living score" },
          {
            key: "daily_living_adaptive_level",
            label: "Daily living adaptive level",
            compute: (data) => getVinelandAdaptiveLevel(data.daily_living_score),
          },
          { key: "socialization_score", label: "Socialization score" },
          {
            key: "socialization_adaptive_level",
            label: "Socialization adaptive level",
            compute: (data) => getVinelandAdaptiveLevel(data.socialization_score),
          },
          { key: "motor_score", label: "Motor score" },
        ],
      },
      {
        title: "Maladaptive",
        fields: [
          {
            key: "behavior_externalizing_v_scale",
            label: "Externalizing behavior qualitative descriptor",
            compute: (data) => {
              const descriptor = getVinelandQualitativeDescriptor(
                data.behavior_externalizing_v_scale
              );
              const score = data.behavior_externalizing_v_scale;
              if (descriptor === null || score === null) return null;
              return `${descriptor} ${score}`;
            },
          },
          {
            key: "behavior_internalizing_v_scale",
            label: "Internalizing behavior qualitative descriptor",
            compute: (data) => {
              const descriptor = getVinelandQualitativeDescriptor(
                data.behavior_internalizing_v_scale
              );
              const score = data.behavior_internalizing_v_scale;
              if (descriptor === null || score === null) return null;
              return `${descriptor} ${score}`;
            },
          },
          {
            key: "total_number_of_critical_items_scored",
            label: "Total number of critical items scored",
          },
        ],
      },
    ],
  },
  {
    type: "cfql2",
    title: "Child and Family Quality of Life, Second Edition (CFQL-2)",
    description: "Tracks child and family quality of life domains over time.",
    primaryActionLabel: "View scores",
    secondaryActionLabel: "Send Assessment",
    secondaryAction: "start",
    filloutUrl: `${START_ASSESSMENT_FILL_OUT_BASE}/1iHL5FNtM6us`,
    chart: {
      variant: "stackedMixed",
      yAxisLabels: ["Total score", "Domain scores"],
      legendLineLabel: "Total score (left axis)",
      legendBarLabel: "Domains (right axis)",
      datasets: [
        {
          label: "Child",
          data: [],
          dataKey: "child_qol_score",
          type: "bar",
          yAxisID: "y1",
          backgroundColor: "#c397e9",
          borderColor: "#c397e9",
        },
        {
          label: "Family",
          data: [],
          dataKey: "family_qol_score",
          type: "bar",
          yAxisID: "y1",
          backgroundColor: "#ec407a",
          borderColor: "#ec407a",
        },
        {
          label: "Caregiver",
          data: [],
          dataKey: "caregiver_qol_score",
          type: "bar",
          yAxisID: "y1",
          backgroundColor: "#42a5f5",
          borderColor: "#42a5f5",
        },
        {
          label: "Financial",
          data: [],
          dataKey: "financial_qol_score",
          type: "bar",
          yAxisID: "y1",
          backgroundColor: "#ffca28",
          borderColor: "#ffca28",
        },
        {
          label: "Social network",
          data: [],
          dataKey: "social_network_qol_score",
          type: "bar",
          yAxisID: "y1",
          backgroundColor: "#ffa726",
          borderColor: "#ffa726",
        },
        {
          label: "Partner relationship",
          data: [],
          dataKey: "partner_relationship_qol_score",
          type: "bar",
          yAxisID: "y1",
          backgroundColor: "#ef5350",
          borderColor: "#ef5350",
        },
        {
          label: "Coping",
          data: [],
          dataKey: "coping_qol_score",
          type: "bar",
          yAxisID: "y1",
          backgroundColor: "#66bb6a",
          borderColor: "#66bb6a",
        },
        {
          label: "Total score",
          data: [],
          dataKey: "total_score",
          type: "line",
          borderColor: "rgba(19, 78, 74, 1)",
          backgroundColor: "rgba(19, 78, 74, 0.3)",
        },
      ],
    },
    resultSections: [
      {
        title: "Scores",
        fields: [
          {
            key: "total_score",
            label: "Total",
          },
          {
            key: "child_qol_score",
            label: "Child QoL",
            tooltip: {
              threshold: 8,
              text: "Consider psychosocial interventions to help child engage with and enjoy their world (ABA plus)",
            },
          },
          {
            key: "family_qol_score",
            label: "Family QoL",
            tooltip: {
              threshold: 8,
              text: "Consider family therapy to improve family dynamics and cohesion",
            },
          },
          {
            key: "caregiver_qol_score",
            label: "Caregiver QoL",
            tooltip: {
              threshold: 8,
              text: "Consider individual therapy, other psychosocial interventions, or other supports for caregiver",
            },
          },
          {
            key: "financial_qol_score",
            label: "Financial QoL",
            tooltip: {
              threshold: 6,
              text: "Assistance in seeking additional resources, avoid (if possible) recommending expensive interventions",
            },
          },
          {
            key: "social_network_qol_score",
            label: "Social Network QoL",
            tooltip: {
              threshold: 8,
              text: "Local options, including community support groups, recreation centers",
            },
          },
          {
            key: "partner_relationship_qol_score",
            label: "Partner Relationship QoL",
            tooltip: {
              threshold: 8,
              text: "Couples or individual therapy, dependent on circumstances",
            },
          },
          {
            key: "coping_qol_score",
            label: "Coping QoL",
            tooltip: {
              threshold: 6,
              text: "Consider full range of individual supports, including respite care, support groups, engaging external support",
            },
          },
          {
            key: "change_score",
            label: "Change",
            tooltip: {
              threshold: 14,
              text: "Look at the domain showing decreases, try to understand why, and make appropriate recommendations",
            },
          },
        ],
      },
    ],
  },
  {
    type: "vbmappMilestones",
    title:
      "Verbal Behavior Milestones Assessment and Placement Program - Milestones (VB-MAPP Milestones)",
    description: "Displays milestone progression across language and learning domains.",
    primaryActionLabel: "View scores",
    secondaryActionLabel: "Add assessment",
    secondaryAction: "add",
    chart: {
      variant: "line",
      yAxisLabels: ["Total score"],
      datasets: [
        {
          label: "Total score",
          data: [],
          dataKey: "total_score",
          borderColor: "rgba(125, 54, 145, 1)",
          backgroundColor: "rgba(125, 54, 145, 0.35)",
        },
      ],
    },
    resultSections: [
      {
        title: "Scores",
        fields: [
          { key: "total_score", label: "Total score" },
          { key: "mand", label: "Mand" },
          { key: "tact", label: "Tact" },
          { key: "listener_responding", label: "Listener responding" },
          {
            key: "visual_perceptual_mts",
            label: "Visual perceptual skills and matching-to-sample",
          },
          { key: "play", label: "Play" },
          { key: "social", label: "Social" },
          { key: "motor_imitation", label: "Motor imitation" },
          { key: "echoic", label: "Echoic" },
          { key: "vocal", label: "Vocal" },
          {
            key: "listener_responding_lrffc",
            label: "Listener responding by feature, function, class",
          },
          { key: "intraverbal", label: "Intraverbal" },
          { key: "group", label: "Group" },
          { key: "linguistics", label: "Linguistics" },
          { key: "reading", label: "Reading" },
          { key: "writing", label: "Writing" },
          { key: "math", label: "Math" },
        ],
      },
    ],
  },
  {
    type: "afls",
    title: "Assessment of Functional Living Skills (AFLS)",
    description: "Shows skill acquisition trends for independent living domains.",
    primaryActionLabel: "View scores",
    secondaryActionLabel: "Add assessment",
    secondaryAction: "add",
    chart: {
      variant: "line",
      yAxisLabels: ["Score"],
      datasets: [
        {
          label: "Basic skills total",
          data: [],
          dataKey: "basic_skills_total",
          borderColor: "rgba(25, 118, 210, 1)",
          backgroundColor: "rgba(25, 118, 210, 0.25)",
        },
        {
          label: "Home skills total",
          data: [],
          dataKey: "home_skills_total",
          borderColor: "rgba(46, 125, 50, 1)",
          backgroundColor: "rgba(46, 125, 50, 0.25)",
        },
        {
          label: "School skills total",
          data: [],
          dataKey: "school_skills_total",
          borderColor: "rgba(251, 192, 45, 1)",
          backgroundColor: "rgba(251, 192, 45, 0.25)",
        },
        {
          label: "Community skills total",
          data: [],
          dataKey: "community_skills_total",
          borderColor: "rgba(156, 39, 176, 1)",
          backgroundColor: "rgba(156, 39, 176, 0.25)",
        },
        {
          label: "Independent living total",
          data: [],
          dataKey: "independent_living_total",
          borderColor: "rgba(0, 121, 107, 1)",
          backgroundColor: "rgba(0, 121, 107, 0.25)",
        },
        {
          label: "Vocational skills total",
          data: [],
          dataKey: "vocational_skills_total",
          borderColor: "rgba(0, 188, 212, 1)",
          backgroundColor: "rgba(0, 188, 212, 0.25)",
        },
      ],
    },
    resultSections: buildAflsResultSections(),
  },
  {
    type: "vbmappBarriers",
    title:
      "Verbal Behavior Milestones Assessment and Placement Program - Barriers (VB-MAPP Barriers)",
    description: "Highlights barriers that can impact skill acquisition and communication.",
    primaryActionLabel: "View scores",
    secondaryActionLabel: "Start Assessment",
    secondaryAction: "start",
    filloutUrl: `${START_ASSESSMENT_FILL_OUT_BASE}/hrjU2SJyB9us`,
    chart: {
      variant: "line",
      yAxisLabels: ["Total score"],
      datasets: [
        {
          label: "Total score",
          data: [],
          dataKey: "total_score",
          borderColor: "rgba(125, 54, 145, 1)",
          backgroundColor: "rgba(125, 54, 145, 0.35)",
        },
      ],
    },
    resultSections: [
      {
        title: "Barrier scores",
        fields: [
          { key: "total_score", label: "Total barrier score" },
          { key: "negative_behaviors", label: "Negative behaviors" },
          { key: "instructional_control", label: "Instructional control" },
          { key: "absent_weak_mand_repertoire", label: "Absent/weak mand repertoire" },
          { key: "absent_weak_tact_repertoire", label: "Absent/weak tact repertoire" },
          { key: "absent_weak_motor_imitation", label: "Absent/weak motor imitation" },
          { key: "absent_weak_echoic_repertoire", label: "Absent/weak echoic repertoire" },
          { key: "absent_weak_visual_perceptual_mts", label: "Absent/weak visual perceptual/MTS" },
          { key: "absent_weak_listener_repertoires", label: "Absent/weak listener repertoires" },
          {
            key: "absent_weak_intraverbal_repertoire",
            label: "Absent/weak intraverbal repertoire",
          },
          { key: "absent_weak_social_skills", label: "Absent/weak social skills" },
          { key: "prompt_dependent", label: "Prompt dependent" },
          { key: "scrolling_responses", label: "Scrolling responses" },
          { key: "impaired_scanning_skills", label: "Impaired scanning skills" },
          {
            key: "failure_conditional_discriminations",
            label: "Failure conditional discriminations",
          },
          { key: "failure_to_generalize", label: "Failure to generalize" },
          {
            key: "weak_atypical_motivating_operations",
            label: "Weak/atypical motivating operations",
          },
          { key: "response_requirement_weakens_mo", label: "Response requirement weakens MO" },
          { key: "reinforcement_dependent", label: "Reinforcement dependent" },
          { key: "self_stimulation", label: "Self-stimulation" },
          { key: "articulation_problems", label: "Articulation problems" },
          { key: "obsessive_compulsive_behavior", label: "Obsessive-compulsive behavior" },
          { key: "hyperactive_behavior", label: "Hyperactive behavior" },
          { key: "failure_to_make_eye_contact", label: "Failure to make eye contact" },
          { key: "sensory_defensiveness", label: "Sensory defensiveness" },
        ],
      },
    ],
  },
  {
    type: "vbmappTransition",
    title:
      "Verbal Behavior Milestones Assessment and Placement Program - Transition (VB-MAPP Transition)",
    description: "Monitors readiness factors for less restrictive learning environments.",
    primaryActionLabel: "View scores",
    secondaryActionLabel: "Start Assessment",
    secondaryAction: "start",
    filloutUrl: `${START_ASSESSMENT_FILL_OUT_BASE}/v4V7hXDP9Fus`,
    chart: {
      variant: "line",
      yAxisLabels: ["Total score"],
      datasets: [
        {
          label: "Total score",
          data: [],
          dataKey: "total_score",
          borderColor: "rgba(125, 54, 145, 1)",
          backgroundColor: "rgba(125, 54, 145, 0.35)",
        },
      ],
    },
    resultSections: [
      {
        title: "Transition readiness",
        fields: [
          { key: "total_score", label: "Total transition score" },
          { key: "vbmapp_milestones_score", label: "VB-MAPP milestones score" },
          { key: "overall_vbmapp_barriers_score", label: "Overall VB-MAPP barriers score" },
          {
            key: "barriers_negative_behaviors_instructional_score",
            label: "Barriers: negative behaviors/instructional",
          },
          {
            key: "milestones_classroom_routines_group_skills",
            label: "Milestones: classroom routines/group skills",
          },
          {
            key: "milestones_social_behavior_social_play",
            label: "Milestones: social behavior/social play",
          },
          {
            key: "works_independently_academic_tasks",
            label: "Works independently on academic tasks",
          },
          { key: "generalization_of_skills", label: "Generalization of skills" },
          { key: "range_of_reinforcers", label: "Range of reinforcers" },
          { key: "rate_of_acquisition_new_skills", label: "Rate of acquisition of new skills" },
          { key: "retention_of_new_skills", label: "Retention of new skills" },
          { key: "learning_from_natural_environment", label: "Learning from natural environment" },
          { key: "transfer_between_verbal_operants", label: "Transfer between verbal operants" },
          { key: "adaptability_to_change", label: "Adaptability to change" },
          { key: "spontaneous_behaviors", label: "Spontaneous behaviors" },
          { key: "self_directed_play_leisure_skills", label: "Self-directed play/leisure skills" },
          { key: "general_self_help_skills", label: "General self-help skills" },
          { key: "toileting_skills", label: "Toileting skills" },
          { key: "eating_skills", label: "Eating skills" },
        ],
      },
    ],
  },
  {
    type: "sociallySavvy",
    title: "Socially Savvy",
    description: "Summarizes social competency and practical social behavior growth.",
    primaryActionLabel: "View scores",
    secondaryActionLabel: "Start Assessment",
    secondaryAction: "start",
    filloutUrl: `${START_ASSESSMENT_FILL_OUT_BASE}/jY41TmyZPuus`,
    chart: {
      variant: "line",
      yAxisLabels: ["Raw scores"],
      datasets: [
        {
          label: "Joint attn. (max 27)",
          data: [],
          dataKey: "joint_attention_total",
          borderColor: "rgba(33, 150, 243, 1)",
          backgroundColor: "rgba(33, 150, 243, 0.3)",
          tooltipValueSuffix: "/27",
        },
        {
          label: "Social play (max 72)",
          data: [],
          dataKey: "social_play_total",
          borderColor: "rgba(76, 175, 80, 1)",
          backgroundColor: "rgba(76, 175, 80, 0.3)",
          tooltipValueSuffix: "/72",
        },
        {
          label: "Self reg. (max 51)",
          data: [],
          dataKey: "self_regulation_total",
          borderColor: "rgba(255, 193, 7, 1)",
          backgroundColor: "rgba(255, 193, 7, 0.3)",
          tooltipValueSuffix: "/51",
        },
        {
          label: "Social/emotional (max 18)",
          data: [],
          dataKey: "social_emotional_total",
          borderColor: "rgba(244, 67, 54, 1)",
          backgroundColor: "rgba(244, 67, 54, 0.3)",
          tooltipValueSuffix: "/18",
        },
        {
          label: "Social language (max 72)",
          data: [],
          dataKey: "social_language_total",
          borderColor: "rgba(142, 66, 202, 1)",
          backgroundColor: "rgba(142, 66, 202, 0.3)",
          tooltipValueSuffix: "/72",
        },
        {
          label: "Classroom/group (max 66)",
          data: [],
          dataKey: "classroom_group_behavior_total",
          borderColor: "rgba(8, 145, 178, 1)",
          backgroundColor: "rgba(8, 145, 178, 0.3)",
          tooltipValueSuffix: "/66",
        },
        {
          label: "Nonverbal social (max 18)",
          data: [],
          dataKey: "nonverbal_social_language_total",
          borderColor: "rgba(194, 65, 12, 1)",
          backgroundColor: "rgba(194, 65, 12, 0.3)",
          tooltipValueSuffix: "/18",
        },
      ],
    },
    resultSections: buildSociallySavvyResultSections(),
  },
  {
    type: "srs2",
    title: "Social Responsiveness Scale (SRS-2)",
    description: "Tracks social responsiveness indicators and symptom severity over time.",
    primaryActionLabel: "View scores",
    secondaryActionLabel: "Add assessment",
    secondaryAction: "add",
    chart: {
      variant: "line",
      yAxisLabels: ["Raw scores"],
      datasets: [
        {
          label: "Awareness T-score",
          data: [],
          dataKey: "awareness_t_score",
          borderColor: "rgba(22, 101, 52, 1)",
          backgroundColor: "rgba(22, 101, 52, 0.35)",
        },
        {
          label: "Cognition T-score",
          data: [],
          dataKey: "cognition_t_score",
          borderColor: "rgba(255, 193, 7, 1)",
          backgroundColor: "rgba(255, 193, 7, 0.35)",
        },
        {
          label: "Communication T-score",
          data: [],
          dataKey: "communication_t_score",
          borderColor: "rgba(33, 150, 243, 1)",
          backgroundColor: "rgba(33, 150, 243, 0.35)",
        },
        {
          label: "Motivation T-score",
          data: [],
          dataKey: "motivation_t_score",
          borderColor: "rgba(244, 67, 54, 1)",
          backgroundColor: "rgba(244, 67, 54, 0.35)",
        },
        {
          label: "SCI T-score",
          data: [],
          dataKey: "srs_sci_t_score",
          borderColor: "rgba(142, 66, 202, 1)",
          backgroundColor: "rgba(142, 66, 202, 0.35)",
        },
        {
          label: "RIRB T-score",
          data: [],
          dataKey: "rirb_t_score",
          borderColor: "rgba(158, 158, 158, 1)",
          backgroundColor: "rgba(158, 158, 158, 0.35)",
        },
      ],
    },
    resultSections: [
      {
        title: "SRS-2 scores",
        fields: [
          { key: "total_t_score", label: "Total T-score" },
          { key: "severity_range", label: "Severity range" },
          { key: "awareness_t_score", label: "Awareness T-score" },
          { key: "awareness_level", label: "Awareness level" },
          { key: "cognition_t_score", label: "Cognition T-score" },
          { key: "cognition_level", label: "Cognition level" },
          { key: "communication_t_score", label: "Communication T-score" },
          { key: "communication_level", label: "Communication level" },
          { key: "motivation_t_score", label: "Motivation T-score" },
          { key: "motivation_level", label: "Motivation level" },
          {
            key: "srs_sci_t_score",
            label: "SRS social communication and interaction T-score",
          },
          {
            key: "srs_sci_level",
            label: "SRS social communication and interaction level",
          },
          {
            key: "rirb_t_score",
            label: "Restricted interests and repetitive behavior T-score",
          },
          {
            key: "rirb_level",
            label: "Restricted interests and repetitive behavior level",
          },
        ],
      },
    ],
  },
  {
    type: "peak",
    title: "PEAK Comprehensive Assessment",
    description: "Evaluates foundational learning processes across verbal behavior domains.",
    primaryActionLabel: "View scores",
    secondaryActionLabel: "Add assessment",
    secondaryAction: "add",
    chart: {
      variant: "line",
      yAxisLabels: ["Raw scores"],
      datasets: [
        {
          label: "Direct teaching total score",
          data: [],
          dataKey: "dt_total_score",
          borderColor: "rgba(22, 101, 52, 1)",
          backgroundColor: "rgba(22, 101, 52, 0.3)",
        },
        {
          label: "Generalization total score",
          data: [],
          dataKey: "g_total_score",
          borderColor: "rgba(255, 193, 7, 1)",
          backgroundColor: "rgba(255, 193, 7, 0.3)",
        },
        {
          label: "Equivalence total score",
          data: [],
          dataKey: "e_total_score",
          borderColor: "rgba(33, 150, 243, 1)",
          backgroundColor: "rgba(33, 150, 243, 0.3)",
        },
        {
          label: "Transformation total score",
          data: [],
          dataKey: "t_total_score",
          borderColor: "rgba(142, 66, 202, 1)",
          backgroundColor: "rgba(142, 66, 202, 0.3)",
        },
      ],
    },
    resultSections: [
      {
        title: "Module totals",
        fields: [
          { key: "dt_total_score", label: "Direct teaching total" },
          { key: "g_total_score", label: "Generalization total" },
          { key: "e_total_score", label: "Equivalence total" },
          { key: "t_total_score", label: "Transformation total" },
        ],
      },
      {
        title: "Direct teaching (DT) module",
        fields: [
          { key: "dt_foundational_learning_score", label: "Foundational learning" },
          { key: "dt_foundational_learning_typical_age_score", label: "FL typical age score" },
          {
            key: "dt_foundational_learning_age_range_equivalent",
            label: "FL age range equivalent",
          },
          { key: "dt_foundational_learning_difference_in_score", label: "FL difference in score" },
          { key: "dt_perceptual_learning_score", label: "Perceptual learning" },
          { key: "dt_perceptual_learning_typical_age_score", label: "PL typical age score" },
          { key: "dt_perceptual_learning_age_range_equivalent", label: "PL age range equivalent" },
          { key: "dt_perceptual_learning_difference_in_score", label: "PL difference in score" },
          { key: "dt_verbal_comprehension_score", label: "Verbal comprehension" },
          { key: "dt_verbal_comprehension_typical_age_score", label: "VC typical age score" },
          { key: "dt_verbal_comprehension_age_range_equivalent", label: "VC age range equivalent" },
          { key: "dt_verbal_comprehension_difference_in_score", label: "VC difference in score" },
          { key: "dt_verbal_reasoning_memory_score", label: "Verbal reasoning & memory" },
          { key: "dt_verbal_reasoning_memory_typical_age_score", label: "VRM typical age score" },
          {
            key: "dt_verbal_reasoning_memory_age_range_equivalent",
            label: "VRM age range equivalent",
          },
          {
            key: "dt_verbal_reasoning_memory_difference_in_score",
            label: "VRM difference in score",
          },
          { key: "dt_total_typical_age_score", label: "DT typical age score" },
          { key: "dt_age_range_equivalent", label: "DT age range equivalent" },
          { key: "dt_total_difference_in_score", label: "DT difference in score" },
        ],
      },
      {
        title: "Generalization (GEN) module",
        fields: [
          {
            key: "g_foundational_learning_basic_social_score",
            label: "Foundational learning & basic social skills",
          },
          {
            key: "g_foundational_learning_basic_social_typical_age_score",
            label: "FL/BS typical age score",
          },
          {
            key: "g_foundational_learning_basic_social_age_range_equivalent",
            label: "FL/BS age range equivalent",
          },
          {
            key: "g_foundational_learning_basic_social_difference_in_score",
            label: "FL/BS difference in score",
          },
          {
            key: "g_basic_verbal_comprehension_advanced_social_score",
            label: "Basic verbal comprehension & advanced social skills",
          },
          {
            key: "g_basic_verbal_comprehension_advanced_social_typical_age_score",
            label: "BV/AS typical age score",
          },
          {
            key: "g_basic_verbal_comprehension_advanced_social_difference_in_score",
            label: "BV/AS difference in score",
          },
          {
            key: "g_advanced_verbal_comprehension_problem_solving_score",
            label: "Advanced verbal comprehension & problem solving",
          },
          {
            key: "g_advanced_verbal_problem_solving_typical_age_score",
            label: "AV/PS typical age score",
          },
          {
            key: "g_advanced_verbal_problem_solving_age_range_equivalent",
            label: "AV/PS age range equivalent",
          },
          {
            key: "g_advanced_verbal_problem_solving_difference_in_score",
            label: "AV/PS difference in score",
          },
          {
            key: "g_verbal_reasoning_advanced_problem_solving_score",
            label: "Verbal reasoning & advanced problem solving",
          },
          {
            key: "g_verbal_reasoning_advanced_typical_age_score",
            label: "VR/APS typical age score",
          },
          {
            key: "g_verbal_reasoning_advanced_age_range_equivalent",
            label: "VR/APS age range equivalent",
          },
          {
            key: "g_verbal_reasoning_advanced_difference_in_score",
            label: "VR/APS difference in score",
          },
          { key: "g_total_typical_age_score", label: "GEN typical age score" },
          { key: "g_age_range_equivalent", label: "GEN age range equivalent" },
          { key: "g_total_difference_in_score", label: "GEN difference in score" },
        ],
      },
      {
        title: "Equivalence (EQ) module",
        fields: [
          { key: "e_reflexivity_score", label: "Reflexivity" },
          { key: "e_symmetry_score", label: "Symmetry" },
          { key: "e_transitivity_score", label: "Transitivity" },
          { key: "e_equivalence_score", label: "Equivalence" },
          { key: "e_total_score", label: "EQ total" },
        ],
      },
      {
        title: "Transformation (T) module",
        fields: [
          { key: "t_non_arbitrary_level1_score", label: "Non-Arbitrary (Level 1)" },
          { key: "t_cultural_level2_score", label: "Cultural (Level 2)" },
          { key: "t_arbitrary_level3_score", label: "Arbitrary (Level 3)" },
          { key: "t_complex_level4_score", label: "Complex (Level 4)" },
          { key: "t_total_score", label: "T total" },
        ],
      },
    ],
  },
  {
    type: "efl",
    title: "Essential for Living: Quick Assessment (EFL)",
    description: "Monitors critical communication and daily living targets.",
    primaryActionLabel: "View scores",
    secondaryActionLabel: "Start Assessment",
    secondaryAction: "start",
    filloutUrl: `${START_ASSESSMENT_FILL_OUT_BASE}/13RinQcHF4us`,
    skipHero: true,
    chart: {
      variant: "line",
      yAxisLabels: ["Scores"],
      datasets: [
        {
          label: "Making requests",
          data: [],
          dataKey: "making_requests",
          borderColor: "rgba(22, 101, 52, 1)",
          backgroundColor: "rgba(22, 101, 52, 0.35)",
        },
        {
          label: "Waiting",
          data: [],
          dataKey: "waiting",
          borderColor: "rgba(33, 150, 243, 1)",
          backgroundColor: "rgba(33, 150, 243, 0.35)",
        },
        {
          label: "Accepting removals, transitions, sharing",
          data: [],
          dataKey: "accepting_removals_transitions_sharing",
          borderColor: "rgba(255, 193, 7, 1)",
          backgroundColor: "rgba(255, 193, 7, 0.35)",
        },
        {
          label: "Completing consecutive brief tasks",
          data: [],
          dataKey: "completing_consecutive_brief_tasks",
          borderColor: "rgba(244, 67, 54, 1)",
          backgroundColor: "rgba(244, 67, 54, 0.35)",
        },
        {
          label: "Accepting 'no'",
          data: [],
          dataKey: "accepting_no",
          borderColor: "rgba(8, 145, 178, 1)",
          backgroundColor: "rgba(8, 145, 178, 0.35)",
        },
        {
          label: "Following directions: health and safety",
          data: [],
          dataKey: "following_directions_health_safety",
          borderColor: "rgba(142, 66, 202, 1)",
          backgroundColor: "rgba(142, 66, 202, 0.35)",
        },
        {
          label: "Completing daily living: health and safety",
          data: [],
          dataKey: "completing_daily_living_health_safety",
          borderColor: "rgba(194, 65, 12, 1)",
          backgroundColor: "rgba(194, 65, 12, 0.35)",
        },
        {
          label: "Tolerating situations: health and safety",
          data: [],
          dataKey: "tolerating_situations_health_safety",
          borderColor: "rgba(76, 175, 80, 1)",
          backgroundColor: "rgba(76, 175, 80, 0.35)",
        },
      ],
    },
    resultSections: [
      {
        title: "EFL scores",
        fields: [
          { key: "spoken_words", label: "Spoken words" },
          { key: "alternative_method_of_speaking", label: "Alternative method of speaking" },
          { key: "making_requests", label: "Making requests" },
          { key: "waiting", label: "Waiting" },
          {
            key: "accepting_removals_transitions_sharing",
            label: "Accepting removals, transitions, sharing",
          },
          {
            key: "completing_consecutive_brief_tasks",
            label: "Completing consecutive brief tasks",
          },
          { key: "accepting_no", label: "Accepting 'no'" },
          {
            key: "following_directions_health_safety",
            label: "Following directions: health and safety",
          },
          {
            key: "completing_daily_living_health_safety",
            label: "Completing daily living: health and safety",
          },
          {
            key: "tolerating_situations_health_safety",
            label: "Tolerating situations: health and safety",
          },
          { key: "matching", label: "Matching" },
          { key: "imitation", label: "Imitation" },
          { key: "other_daily_living_skills", label: "Other daily living skills" },
          { key: "tolerating_other_situations", label: "Tolerating other situations" },
          { key: "naming_and_describing", label: "Naming and describing" },
          {
            key: "following_directions_recognizing_retrieving",
            label: "Following directions: recognizing/retrieving",
          },
          { key: "answering_questions", label: "Answering questions" },
          { key: "problem_behavior", label: "Problem behavior" },
        ],
      },
    ],
  },
  {
    type: "qabf",
    title: "Questions about Behavioral Function (QABF)",
    description: "Breaks down likely behavioral function categories over time.",
    primaryActionLabel: "View scores",
    secondaryActionLabel: "Start Assessment",
    secondaryAction: "start",
    filloutUrl: `${START_ASSESSMENT_FILL_OUT_BASE}/7nBMf4EXxRus`,
    skipHero: true,
    chart: {
      variant: "bar",
      yAxisLabels: ["Totals"],
      datasets: [
        {
          label: "Attention",
          data: [],
          dataKey: "attention",
          type: "bar",
          borderColor: "rgba(171, 128, 215, 1)",
          backgroundColor: "rgba(171, 128, 215, 0.75)",
        },
        {
          label: "Escape",
          data: [],
          dataKey: "escape",
          type: "bar",
          borderColor: "rgba(236, 64, 122, 1)",
          backgroundColor: "rgba(236, 64, 122, 0.75)",
        },
        {
          label: "Non-social",
          data: [],
          dataKey: "non_social",
          type: "bar",
          borderColor: "rgba(41, 182, 246, 1)",
          backgroundColor: "rgba(41, 182, 246, 0.75)",
        },
        {
          label: "Physical",
          data: [],
          dataKey: "physical",
          type: "bar",
          borderColor: "rgba(255, 193, 7, 1)",
          backgroundColor: "rgba(255, 193, 7, 0.75)",
        },
        {
          label: "Tangible",
          data: [],
          dataKey: "tangible",
          type: "bar",
          borderColor: "rgba(255, 152, 0, 1)",
          backgroundColor: "rgba(255, 152, 0, 0.75)",
        },
      ],
    },
    resultSections: [
      {
        title: "Behavioral functions",
        fields: [
          { key: "behavior_reduction_goal", label: "Behavior reduction goal" },
          { key: "topography", label: "Topography" },
          { key: "operational_definition", label: "Operational definition" },
          { key: "attention", label: "Attention" },
          { key: "escape", label: "Escape" },
          { key: "non_social", label: "Nonsocial" },
          { key: "physical", label: "Physical" },
          { key: "tangible", label: "Tangible" },
          { key: "hypothesized_function", label: "Hypothesized function" },
        ],
      },
    ],
  },
  {
    type: "cpfq",
    title: "Child Psychological Flexibility Questionnaire (CPFQ)",
    description: "Shows psychological flexibility and resilience trend data.",
    primaryActionLabel: "View scores",
    secondaryActionLabel: "Start assessment",
    secondaryAction: "start",
    filloutUrl: `${START_ASSESSMENT_FILL_OUT_BASE}/5k6cdS8rM8us`,
    chart: {
      variant: "line",
      yAxisLabels: ["Values"],
      datasets: [
        {
          label: "Present moment",
          data: [],
          dataKey: "child_present_moment",
          borderColor: "rgba(37, 99, 235, 1)",
          backgroundColor: "rgba(37, 99, 235, 0.35)",
          tooltipValueSuffix: " / 16",
        },
        {
          label: "Acceptance",
          data: [],
          dataKey: "child_acceptance",
          borderColor: "rgba(34, 197, 94, 1)",
          backgroundColor: "rgba(34, 197, 94, 0.35)",
          tooltipValueSuffix: " / 16",
        },
        {
          label: "Defusion",
          data: [],
          dataKey: "child_defusion",
          borderColor: "rgba(234, 179, 8, 1)",
          backgroundColor: "rgba(234, 179, 8, 0.35)",
          tooltipValueSuffix: " / 16",
        },
        {
          label: "Self-as-context",
          data: [],
          dataKey: "child_self_as_context",
          borderColor: "rgba(168, 85, 247, 1)",
          backgroundColor: "rgba(168, 85, 247, 0.35)",
          tooltipValueSuffix: " / 16",
        },
        {
          label: "Values",
          data: [],
          dataKey: "child_values",
          borderColor: "rgba(5, 150, 105, 1)",
          backgroundColor: "rgba(5, 150, 105, 0.35)",
          tooltipValueSuffix: " / 16",
        },
        {
          label: "Committed action",
          data: [],
          dataKey: "child_committed_action",
          borderColor: "rgba(6, 182, 212, 1)",
          backgroundColor: "rgba(6, 182, 212, 0.35)",
          tooltipValueSuffix: " / 16",
        },
      ],
    },
    resultSections: [
      {
        title: "Psychological flexibility domains (child report)",
        fields: [
          { key: "child_present_moment", label: "Present moment" },
          { key: "child_acceptance", label: "Acceptance" },
          { key: "child_defusion", label: "Defusion" },
          { key: "child_self_as_context", label: "Self as context" },
          { key: "child_values", label: "Values" },
          { key: "child_committed_action", label: "Committed action" },
          { key: "child_total_score", label: "Total CPFQ score (child)" },
        ],
      },
      {
        title: "Psychological flexibility domains (caregiver report)",
        fields: [
          { key: "caregiver_present_moment", label: "Present moment" },
          { key: "caregiver_acceptance", label: "Acceptance" },
          { key: "caregiver_defusion", label: "Defusion" },
          { key: "caregiver_self_as_context", label: "Self as context" },
          { key: "caregiver_values", label: "Values" },
          { key: "caregiver_committed_action", label: "Committed action" },
          { key: "caregiver_total_score", label: "Total CPFQ score (caregiver)" },
        ],
      },
    ],
  },
];

const TYPE_ALIASES: Record<string, AssessmentType> = {
  vineland: "vineland",
  vineland3: "vineland",
  vinelandadaptivebehaviorscalesthirdedition: "vineland",
  cfql2: "cfql2",
  childandfamilyqualityoflifesecondeditioncfql2: "cfql2",
  vbmappmilestones: "vbmappMilestones",
  verbalbehaviormilestonesassessmentandplacementprogrammiletones: "vbmappMilestones",
  verbalmilestonesassessmentandplacementprogrammiletones: "vbmappMilestones",
  afls: "afls",
  assessmentoffunctionallivingskills: "afls",
  vbmappbarriers: "vbmappBarriers",
  vbmapptransition: "vbmappTransition",
  vbmaptransition: "vbmappTransition",
  sociallysavvy: "sociallySavvy",
  srs2: "srs2",
  socialresponsivenessscale: "srs2",
  peak: "peak",
  peakcomprehensiveassessment: "peak",
  efl: "efl",
  essentialforlivingquickassessmentefl: "efl",
  qabf: "qabf",
  questionsaboutbehavioralfunction: "qabf",
  cpfq: "cpfq",
  childpsychologicalflexibilityquestionnaire: "cpfq",
};

function normalizeType(value: string | null | undefined): string {
  return (value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

export function getAssessmentDefinition(type: AssessmentType): AssessmentDefinition {
  return ASSESSMENT_DEFINITIONS.find((item) => item.type === type) || ASSESSMENT_DEFINITIONS[0];
}

export function getAssessmentTypeFromRecord(record: AssessmentRecord): AssessmentType {
  const rawType =
    record.assessment_type ||
    record.type ||
    record.assessment_name ||
    (record.data.assessment_type as string | undefined) ||
    (record.data.type as string | undefined);

  const normalized = normalizeType(rawType);
  return TYPE_ALIASES[normalized] || "vineland";
}

export function getAssessmentRecordsByType(records: AssessmentRecord[]) {
  return records.reduce(
    (acc, record) => {
      const type = getAssessmentTypeFromRecord(record);
      acc[type].push(record);
      return acc;
    },
    {
      vineland: [],
      cfql2: [],
      vbmappMilestones: [],
      afls: [],
      vbmappBarriers: [],
      vbmappTransition: [],
      sociallySavvy: [],
      srs2: [],
      peak: [],
      efl: [],
      qabf: [],
      cpfq: [],
    } as Record<AssessmentType, AssessmentRecord[]>
  );
}
